/**
 * Utilities for handling CSV data
 */

import { IReportData } from './Interfaces';

/**
 * Parse CSV text into an array of objects
 * @param csvText - Raw CSV text content
 * @returns Array of parsed data objects
 */
export const parseCsvData = (csvText: string): IReportData[] => {
  try {
    // Split CSV into rows
    const rows = csvText.split('\n');
    
    if (rows.length === 0) {
      throw new Error('CSV file is empty');
    }
    
    // Clean headers to remove any carriage returns or whitespace
    const headers = rows[0].split(',').map(header => header.trim().replace(/\r$/, ''));
    
    // Process data rows
    return rows.slice(1)
      .filter(row => row.trim() !== '')
      .map(row => {
        const values = row.split(',');
        const record: any = {};
        
        headers.forEach((header, index) => {
          // Convert numeric values to numbers
          if (['gross_income', 'net_income', 'platform_fee'].includes(header)) {
            record[header] = parseFloat(values[index] || '0');
          } else {
            record[header] = values[index] || '';
          }
        });
        
        return record as IReportData;
      });
  } catch (error) {
    console.error('Error parsing CSV data:', error);
    throw error;
  }
};

/**
 * Fetch CSV data from a URL
 * @param url - URL to fetch CSV data from
 * @returns Promise resolving to parsed CSV data
 */
export const fetchCsvFromUrl = async (url: string): Promise<IReportData[]> => {
  try {
    console.log('Fetching CSV from URL:', url);
    
    // Use fetch with appropriate headers to avoid CORS issues
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Accept': 'text/csv,text/plain,*/*'
      },
      mode: 'cors'
    });
    
    if (!response.ok) {
      throw new Error(`Failed to fetch CSV: ${response.status} ${response.statusText}`);
    }
    
    const csvText = await response.text();
    console.log('CSV content received, length:', csvText.length);
    
    return parseCsvData(csvText);
  } catch (error) {
    console.error('Error fetching CSV data:', error);
    throw error;
  }
};

/**
 * Download CSV file from URL
 * @param url - URL of the CSV file
 * @param filename - Name to save the file as
 */
export const downloadCsvFile = (url: string, filename: string): void => {
  // Create a temporary link element to trigger download
  const link = document.createElement('a');
  link.href = url;
  link.download = filename || 'download.csv';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
